import { CURRENCIES, type Currency } from '@/shared/currencies';
import { parseGridDate } from '@/shared/formula/dateSerial';
import type { GridDataFormat, GridFormatKey } from './formats';

/**
 * The type a calculated column takes on its own when its formula is confirmed.
 *
 * Only a column the user has not typed yet (`auto` — "By cell" — or `text`) is
 * retyped, and only when more than half of its non-empty values are of one
 * non-text kind. The heading then refines a number: a currency word or code
 * makes it currency, a percent word makes it a percentage.
 */

const DATE_FUNCTIONS = /^\s*=?\s*(TODAY|NOW|DATE|DATEVALUE|EDATE|EOMONTH|WORKDAY|WORKDAY\.INTL|HOY|AHORA|FECHA|FECHANUMERO|FECHA\.MES|FIN\.MES|DIA\.LAB)\s*\(/i;
const CURRENCY_WORDS = /\b(price|prices|precio|precios|cost|costs|costo|costos|amount|importe|monto|valor|value|balance|saldo|total|fee|fees|comision|comisión|pnl|profit|ganancia)\b/i;
const PERCENT_WORDS = /%|\b(percent|percentage|porcentaje|pct|rate|tasa|yield|rendimiento|return|retorno)\b/i;

export function headerCurrency(title: string): Currency | undefined {
  const upper = title.toUpperCase();
  return CURRENCIES.find((code) => new RegExp(`(^|[^A-Z])${code}([^A-Z]|$)`).test(upper));
}

export function detectColumnType(
  values: readonly unknown[],
  title: string,
  formulaText = '',
): Pick<GridDataFormat, 'type' | 'formatKey' | 'currency'> | null {
  const filled = values.filter((value) => value !== null && value !== undefined && value !== '');
  if (filled.length === 0) return null;
  let numbers = 0;
  let dates = 0;
  for (const value of filled) {
    if (typeof value === 'number' && Number.isFinite(value)) numbers++;
    else if (value instanceof Date || (typeof value === 'string' && /\d/.test(value) && parseGridDate(value))) dates++;
  }
  const half = filled.length / 2;
  if (numbers > half && DATE_FUNCTIONS.test(formulaText)) return { type: 'date', formatKey: 'date' };
  if (dates > half) return { type: 'date', formatKey: 'date' };
  if (numbers <= half) return null;

  const currency = headerCurrency(title);
  if (currency) return { type: 'number', formatKey: 'currency', currency };
  if (PERCENT_WORDS.test(title)) return { type: 'number', formatKey: 'percent' };
  if (CURRENCY_WORDS.test(title)) return { type: 'number', formatKey: 'currency' };
  const key: GridFormatKey = filled.every((value) => typeof value !== 'number' || Number.isInteger(value)) ? 'integer' : 'decimal';
  return { type: 'number', formatKey: key };
}
